import * as Yup from 'yup';

// Validation schema for a single question
const questionSchema = Yup.object().shape({
  id: Yup.string().required(),
  question: Yup.string()
    .trim()
    .required('Question text is required')
    .min(1, 'Question text is required'),
  question_type: Yup.string()
    .oneOf(['free_text', 'single_choice', 'multiple_choice'])
    .required('Question type is required'),
  is_dropdown: Yup.boolean(),
  response_choices: Yup.array()
    .of(
      Yup.string().when('$questionType', {
        is: (questionType: string) => ['single_choice', 'multiple_choice'].includes(questionType),
        then: (schema) => schema.test(
          'is-not-empty',
          'Option text is required',
          (value) => !value || value.trim() !== '' || true // Allow empty for now, will filter in UI
        ),
        otherwise: (schema) => schema
      })
    )
    .when(['question_type', 'is_dropdown'], {
      is: (question_type: string, is_dropdown: boolean) =>
        ['single_choice', 'multiple_choice'].includes(question_type) || is_dropdown,
      then: (schema) => schema
        .test(
          'has-valid-choices',
          'At least one non-empty option is required',
          (value) => {
            if (!value || !Array.isArray(value)) return false;
            return value.some((choice) => choice && choice.trim() !== '');
          }
        )
        .test(
          'all-non-empty',
          'All options must have text or be removed',
          (value) => {
            if (!value || !Array.isArray(value)) return true;
            // All options must either be non-empty or the array should only have non-empty options
            return value.every((choice) => choice && choice.trim() !== '');
          }
        )
        .test(
          'no-duplicates',
          'Duplicate options are not allowed',
          (value) => {
            if (!value || !Array.isArray(value)) return true;
            // Check for case-sensitive duplicates (trimmed)
            const seenOptions = new Set<string>();
            for (const choice of value) {
              const trimmed = choice?.trim();
              // Skip empty options (they have their own validation)
              if (!trimmed) continue;
              if (seenOptions.has(trimmed)) {
                return false; // Duplicate found
              }
              seenOptions.add(trimmed);
            }
            return true;
          }
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  required: Yup.boolean(),
  comments_allowed: Yup.boolean(),
  parent_question_id: Yup.string().nullable(),
  parent_question_response_option: Yup.string().nullable(),
  question_number: Yup.string(),
});

// Validation schema for the entire questionnaire form
export const questionnaireFormSchema = Yup.object().shape({
  questionnaireId: Yup.string(),
  questions: Yup.array().of(questionSchema),
});

// Type for the form values (can be used for type safety)
export type QuestionnaireFormValues = Yup.InferType<typeof questionnaireFormSchema>;

// Validation schema for intake form questions (supports file_upload type)
const intakeFormQuestionSchema = Yup.object().shape({
  id: Yup.string().required(),
  question: Yup.string()
    .trim()
    .required('Question text is required')
    .min(1, 'Question text is required')
    .test(
      'starts-with-letter',
      'Question must start with a letter.',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        const trimmed = value.trim();
        if (!trimmed) return true;
        // Check if first character is a letter (A-Z, a-z, including Unicode letters)
        return /^[a-zA-Z]/.test(trimmed);
      }
    ),
  question_type: Yup.string()
    .oneOf(['free_text', 'single_choice', 'multiple_choice', 'file_upload'])
    .required('Question type is required'),
  is_dropdown: Yup.boolean(),
  response_choices: Yup.array()
    .of(
      Yup.string().when('$questionType', {
        is: (questionType: string) => ['single_choice', 'multiple_choice'].includes(questionType),
        then: (schema) => schema.test(
          'is-not-empty',
          'Option text is required',
          (value) => !value || value.trim() !== '' || true
        ),
        otherwise: (schema) => schema
      })
    )
    .when(['question_type', 'is_dropdown'], {
      is: (question_type: string, is_dropdown: boolean) =>
        ['single_choice', 'multiple_choice'].includes(question_type) || is_dropdown,
      then: (schema) => schema
        .test(
          'has-valid-choices',
          'At least one non-empty option is required',
          (value) => {
            if (!value || !Array.isArray(value)) return false;
            return value.some((choice) => choice && choice.trim() !== '');
          }
        )
        .test(
          'all-non-empty',
          'All options must have text or be removed',
          (value) => {
            if (!value || !Array.isArray(value)) return true;
            return value.every((choice) => choice && choice.trim() !== '');
          }
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  max_files: Yup.number().when("question_type", {
    is: "file_upload",
    then: (schema) =>
      schema
        .min(1, "At least 1 file is required")
        .max(50, "Maximum 50 files can be requested"),
    otherwise: (schema) => schema.notRequired(),
  }),
  required: Yup.boolean(),
  field_name: Yup.string(),
  hidden_from_user: Yup.boolean(),
});

// Validation schema for the entire intake form
export const intakeFormSchema = Yup.object().shape({
  questions: Yup.array().of(intakeFormQuestionSchema).required().default([]),
  totalFilesRequested: Yup.number()
    .max(50, "Total requested files across all file upload questions cannot exceed 50")
    .default(0),
});

// Type for intake form values - use Yup.InferType for accurate type inference
export type IntakeFormValues = Yup.InferType<typeof intakeFormSchema>;
